import { useEffect } from "react";
import type { Project } from "@/state/app-types";
import {
  buildDefaultProjectConnectionConfig,
  buildDefaultProjectHostContext,
  pickProjectConnectionConfig,
  pickProjectHostContext,
} from "@/lib/client-config";
import { useClientConfigStore } from "@/stores/client-config-store";
import { useHostContextStore } from "@/stores/client-context-store";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import {
  SAFE_AREA_INSETS_NONE,
  useUIPlaygroundStore,
} from "@/stores/ui-playground-store";

interface ProjectClientConfigSyncProps {
  activeProjectId: string;
  savedClientConfig?: Project["clientConfig"];
}

export function ProjectClientConfigSync({
  activeProjectId,
  savedClientConfig,
}: ProjectClientConfigSyncProps) {
  const themeMode = usePreferencesStore((state) => state.themeMode);
  const displayMode = useUIPlaygroundStore(
    (state) => state.globals.displayMode,
  );
  const locale = useUIPlaygroundStore((state) => state.globals.locale);
  const timeZone = useUIPlaygroundStore((state) => state.globals.timeZone);
  const hover = useUIPlaygroundStore((state) => state.capabilities.hover);
  const touch = useUIPlaygroundStore((state) => state.capabilities.touch);

  useEffect(() => {
    const defaultConnectionConfig = buildDefaultProjectConnectionConfig();
    const defaultHostContext = buildDefaultProjectHostContext({
      theme: themeMode,
      displayMode,
      locale,
      timeZone,
      deviceCapabilities: { hover, touch },
      safeAreaInsets: SAFE_AREA_INSETS_NONE,
    });

    useClientConfigStore.getState().loadProjectConfig({
      projectId: activeProjectId,
      defaultConfig: defaultConnectionConfig,
      savedConfig: savedClientConfig
        ? pickProjectConnectionConfig(savedClientConfig)
        : undefined,
    });
    useHostContextStore.getState().loadProjectHostContext({
      projectId: activeProjectId,
      defaultHostContext,
      savedHostContext: savedClientConfig
        ? pickProjectHostContext(savedClientConfig)
        : undefined,
    });
  }, [
    activeProjectId,
    savedClientConfig,
    themeMode,
    displayMode,
    locale,
    timeZone,
    hover,
    touch,
  ]);

  return null;
}
